import React from "react";
import PropTypes from "prop-types";

/**
 * Shared Subscription Banner Illustration matching the modern Brand Dashboard theme.
 * Replaces generic/raw feather icons with polished, themed SVG illustrations.
 */
export const SubscriptionBannerIllustration = ({
  type = "membership",
  size = 22,
}) => {
  switch (type) {
    case "scheduled":
    case "success":
    case "check-circle":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            fill="#12B76A"
            fillOpacity="0.16"
            stroke="#12B76A"
            strokeWidth="1.8"
          />
          <path
            d="M8.5 12.3L10.8 14.6L15.5 9.5"
            stroke="#12B76A"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case "warning":
    case "retry":
    case "alert-triangle":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M12 3.5L20.8 19H3.2L12 3.5Z"
            fill="#F79009"
            fillOpacity="0.16"
            stroke="#F79009"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M12 9V13.5"
            stroke="#F79009"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="16.5" r="1.1" fill="#F79009" />
        </svg>
      );

    case "danger":
    case "expired":
    case "suspended":
    case "alert-octagon":
    case "alert-circle":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M7.86 3H16.14L21 7.86V16.14L16.14 21H7.86L3 16.14V7.86L7.86 3Z"
            fill="#D92D20"
            fillOpacity="0.16"
            stroke="#D92D20"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M12 8V12.5"
            stroke="#D92D20"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="15.8" r="1.1" fill="#D92D20" />
        </svg>
      );

    case "invoice":
    case "file-text":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M14 3H6C4.9 3 4 3.9 4 5V19C4 20.1 4.9 21 6 21H18C19.1 21 20 20.1 20 19V9L14 3Z"
            fill="#232738"
            fillOpacity="0.12"
            stroke="#232738"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M14 3V9H20"
            stroke="#232738"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M8 13H16"
            stroke="#232738"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M8 17H13"
            stroke="#232738"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );

    case "clock":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            fill="#232738"
            fillOpacity="0.1"
            stroke="#232738"
            strokeWidth="1.8"
          />
          <path
            d="M12 7V12L15 14"
            stroke="#232738"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case "calendar":
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <rect
            x="3"
            y="4"
            width="18"
            height="17"
            rx="2"
            fill="#232738"
            fillOpacity="0.1"
            stroke="#232738"
            strokeWidth="1.8"
          />
          <path
            d="M16 2V6"
            stroke="#232738"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M8 2V6"
            stroke="#232738"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path d="M3 10H21" stroke="#232738" strokeWidth="1.8" />
          <circle cx="8" cy="14" r="1" fill="#232738" />
          <circle cx="12" cy="14" r="1" fill="#232738" />
          <circle cx="16" cy="14" r="1" fill="#232738" />
        </svg>
      );

    case "membership":
    case "award":
    default:
      return (
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="9.5"
            r="6"
            fill="#232738"
            fillOpacity="0.12"
            stroke="#232738"
            strokeWidth="1.75"
          />
          <path
            d="M12 7L12.7 8.5L14.4 8.7L13.1 9.9L13.5 11.5L12 10.7L10.5 11.5L10.9 9.9L9.6 8.7L11.3 8.5L12 7Z"
            fill="#232738"
          />
          <path
            d="M9 15L7.5 21L12 18.5L16.5 21L15 15"
            stroke="#232738"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
  }
};

SubscriptionBannerIllustration.propTypes = {
  type: PropTypes.string,
  size: PropTypes.number,
};

export default SubscriptionBannerIllustration;

